import fastdom from 'fastdom';
import breakpoints from 'utils/breakpoints';
import Throttle from 'utils/Throttle';

/**
 * Sets up Centennial Theme spoke menu scroll behaviors.
 */
export default class SpokeMenu {
  /**
   * Create a new SpokeMenu
   */
  constructor({ element, children, options }) {
    this.element = element;
    this.children = children;
    this.options = options;

    // Bind class methods.
    this.init = this.init.bind(this);
    this.handleBreakpoints = this.handleBreakpoints.bind(this);
    this.setScrollThreshold = this.setScrollThreshold.bind(this);
    this.handleIntersections = this.handleIntersections.bind(this);
    this.handleScroll = this.handleScroll.bind(this);
    this.updateMenuAttributes = this.updateMenuAttributes.bind(this);

    // Initialize the header height, then listen for breakpoint changes.
    this.handleBreakpoints();
    breakpoints.lgMin.addListener(this.handleBreakpoints);
  }

  /**
   * Handle breakpoint changes.
   *
   * The menu should only be highlighted for the large breakpoint, since it's
   * not sticky on smaller screens.
   */
  handleBreakpoints() {
    if (breakpoints.lgMin.matches) {
      this.init();
    } else {
      this.destroy();
    }
  }

  /**
   * Handle scroll events.
   */
  handleScroll() {
    if (0 === this.intersecting.length) {
      return;
    }

    const currentScroll = window.pageYOffset;
    const isScrollingDown = (currentScroll > this.previousScroll);

    // Check scroll position for each intersecting section.
    for (let i = this.intersecting.length - 1; 0 <= i; i -= 1) {
      fastdom.measure(() => {
        const spoke = this.spokeMap[this.intersecting[i]];
        const scrollBoundary = (
          spoke.element.offsetTop - this.threshold
        );

        if (isScrollingDown) {
          if (scrollBoundary < currentScroll) {
            this.currentSpoke = spoke.element;
          }
        } else {
          // When scrolling up we need to activate the *previous* spoke.
          const spokeIndex = Object.keys(this.spokeMap)
            .indexOf(this.intersecting[i]);

          if (scrollBoundary > currentScroll && 0 < spokeIndex) {
            const previousId = Object.keys(this.spokeMap)[spokeIndex - 1];
            this.currentSpoke = this.spokeMap[previousId].element;
          }
        }

        // "Activate" the menu item.
        if (undefined !== this.currentSpoke) {
          fastdom.mutate(this.updateMenuAttributes);
        }
      });
    }

    // Save the scroll position.
    this.previousScroll = currentScroll;
  }

  /**
   * Save intersecting element's ID attribute to reduce the loop iteration in
   * the scroll event handler.
   *
   * @param {Object} entries Intersection Observer change values.
   */
  handleIntersections(entries) {
    entries.forEach((entry) => {
      const { id } = entry.target;

      if (entry.isIntersecting) {
        this.intersecting.push(id);
      } else {
        // Filter it out when it's no longer intersecting.
        this.intersecting = this.intersecting
          .filter((intersectId) => intersectId !== id);
      }
    });
  }

  /**
   * Set the point at which the scroll threshold has been met.
   *
   * @return {Number}
   */
  setScrollThreshold() {
    this.threshold = (window.innerHeight / 2);
  }

  /**
   * Update menu item attributes.
   */
  updateMenuAttributes() {
    const id = this.currentSpoke.getAttribute('id');
    const activeLink = this.element.querySelector('[aria-current="true"]');

    // Remove the attribute if present on any element.
    if (null !== activeLink) {
      activeLink.removeAttribute('aria-current');
    }

    // Add the attribute to the current menu item.
    if (null !== this.spokeMap[id].link) {
      this.spokeMap[id].link.setAttribute('aria-current', 'true');
    }
  }

  /**
   * Monitor spokes as they scroll into view and highlight the active one.
   */
  init() {
    /**
     * Maps section IDs to spokes and menu items.
     *
     * @return {Object}
     */
    this.spokeMap = Array.from(this.element.children)
      .reduce((acc, menuItem) => {
        const link = menuItem.querySelector('a');

        if (null !== link) {
          const id = link.getAttribute('href').replace('#', '');
          const element = document.querySelector(`[id="${id}"]`);

          if (null !== element) {
            return {
              ...acc,
              [id]: {
                id,
                element,
                menuItem,
                link,
              },
            };
          }
        }

        return acc;
      }, {});

    /**
     * Holds any intersecting entries.
     *
     * @type {Array}
     */
    this.intersecting = [];

    /**
     * Previous scroll position used to derive the scroll direction.
     *
     * @type {Number}
     */
    this.previousScroll = 0;

    // Initialize the scroll boundary.
    this.setScrollThreshold();

    // Create the intersection observer.
    const observer = new IntersectionObserver(this.handleIntersections);

    // Observe each spoke.
    Object.keys(this.spokeMap).forEach((id) => {
      observer.observe(this.spokeMap[id].element);
    });

    // Listen for events.
    this.scrollListener = new Throttle(window, 'scroll', this.handleScroll);
    this.resizeListener = new Throttle(
      window,
      'resize',
      this.setScrollThreshold,
    );
  }

  /**
   * Destroy the nav highlighter.
   */
  destroy() {
    // The `destroy` method comes from Throttle.
    if (this.scrollListener instanceof Throttle) {
      this.scrollListener.destroy();
    }

    if (this.resizeListener instanceof Throttle) {
      this.resizeListener.destroy();
    }

    const activeLink = this.element.querySelector('[aria-current="true"]');

    // Remove the attribute if present on any element.
    if (null !== activeLink) {
      activeLink.removeAttribute('aria-current');
    }
  }
}
